import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState, type ReactNode } from 'react'
import { ApiError } from '../api/client'
import { cqDeviceSettingsQuery, saveCqDeviceSettings, type CqDeviceSettings, type CqMode } from '../api/connectionQuality'
import { toast } from '../ui/notify'
import { SPEED_HELP, SpeedHourSelect, hourLabel } from './CqSpeedDaily'
import { WifiDiagHelp } from './WifiDiagHelp'
import './CqWifi.css'
import './DeviceConnectionQualityCard.css'

/**
 * Cardul Connection Quality din Agent Settings -> Functions.
 *
 * NU e cardul "Network Monitoring" de deasupra lui (`network_monitor`) - ala e
 * Neighbor Watch: masina asta da ping ALTOR aparate. Connection Quality
 * masoara legatura masinii INSESI: routerul, furnizorul, tinta.
 *
 * Trei stari: mosteneste de la firma (implicitul din Settings -> Install
 * Options), aprins, stins. Cine n-are dreptul (sau aparatul nu e al firmei)
 * nu vede cardul deloc.
 *
 * Faza 3: bifa "Allow Wi-Fi diagnostics" merge pe aceeasi mostenire - cand
 * aparatul ia implicitul firmei, ia si bifa firmei; cand alege On/Off, bifa
 * e a lui.
 *
 * Faza 4: "Daily speed test" si ora lui (ora locala a aparatului), pe aceeasi
 * mostenire, cu explicatia a cata banda consuma.
 */
export function DeviceConnectionQualityCard({ deviceId }: { deviceId: number }) {
  const { data, error } = useQuery(cqDeviceSettingsQuery(deviceId))
  if (error || !data) return null
  return (
    <Card
      key={`${data.mode}:${data.target ?? ''}:${data.interval_s}:${data.wifi_diag ? 1 : 0}:${data.speed_auto ? 1 : 0}:${data.speed_hour ?? 3}`}
      page={data}
    />
  )
}

function Card({ page }: { page: CqDeviceSettings }) {
  const query = useQueryClient()
  const [mode, setMode] = useState<CqMode>(page.mode)
  const [custom, setCustom] = useState(page.target !== null && page.target !== page.defaultTarget)
  const [target, setTarget] = useState(page.target ?? '')
  const [interval, setInterval] = useState(String(page.interval_s))
  const [wifiDiag, setWifiDiag] = useState(page.wifi_diag ?? false)
  const [speedAuto, setSpeedAuto] = useState(page.speed_auto ?? false)
  const [speedHour, setSpeedHour] = useState(page.speed_hour ?? 3)
  const [problem, setProblem] = useState<{ field: string; message: string } | null>(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => setProblem(null), [mode, custom, target, interval, speedHour])

  const on = mode === 'on' || (mode === 'inherit' && page.company.enabled)
  const locked = !page.canEdit || saving

  const save = async () => {
    setSaving(true)
    try {
      await saveCqDeviceSettings(page.device.id, {
        mode,
        target: custom ? target.trim() : '',
        interval_s: Number(interval),
        ...(mode === 'inherit' ? {} : { wifi_diag: wifiDiag, speed_auto: speedAuto, speed_hour: speedHour }),
      })
      toast('Saved.', 'Connection Quality')
      await query.invalidateQueries({ queryKey: ['device', page.device.id, 'connection-quality'] })
    } catch (e) {
      const field = e instanceof ApiError ? String(e.details?.field ?? '') : ''
      const message = e instanceof ApiError && e.message ? e.message : 'It could not be saved.'
      setProblem({ field, message })
      toast(message, 'Connection Quality', 'error')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="af-grid af-grid-one">
      <div className={`af-card af-card-wide${on ? ' on' : ''}`} data-flag="connection_quality">
        <div className="af-icon cq-icon">
          <i className="fas fa-signal"></i>
        </div>
        <h4>Connection Quality</h4>
        <p>
          This machine measures its own connection: it pings the <strong>router</strong>, the{' '}
          <strong>internet provider</strong> and the <strong>target</strong>, times DNS, TCP, TLS and the first byte of a
          request to the target, and counts TCP retransmissions. The results are on the{' '}
          <strong>Connection Quality</strong> tab in Proactivitee. Off by default.
        </p>
        <form
          className="cq-form"
          onSubmit={(e) => {
            e.preventDefault()
            void save()
          }}
        >
          <div className="af-row" role="radiogroup" aria-label="Connection Quality">
            <span className="af-k">State</span>
            <span className="af-v">
              <ModeChoice value="inherit" mode={mode} disabled={locked} onPick={setMode}>
                Company default ({page.company.enabled ? 'on' : 'off'})
              </ModeChoice>
              <ModeChoice value="on" mode={mode} disabled={locked} onPick={setMode}>
                On
              </ModeChoice>
              <ModeChoice value="off" mode={mode} disabled={locked} onPick={setMode}>
                Off
              </ModeChoice>
            </span>
          </div>
          {mode === 'inherit' ? (
            <p className="cq-inherit">
              Company default: <strong>{page.company.enabled ? 'on' : 'off'}</strong>, target{' '}
              <strong>{page.company.target}</strong>, every <strong>{page.company.interval_s} s</strong>. It is set in{' '}
              <a href="/app/settings/connection_quality" className="cq-company-link">
                Company default &rarr;
              </a>
              . Wi-Fi diagnostics: <strong>{page.company.wifi_diag ? 'allowed' : 'not allowed'}</strong>.
              Daily speed test:{' '}
              <strong>{page.company.speed_auto ? `on, at ${hourLabel(page.company.speed_hour ?? 3)} device time` : 'off'}</strong>.
            </p>
          ) : (
            <>
              <div className="af-row">
                <span className="af-k">Target</span>
                <span className="af-v">
                  <label className="af-check">
                    <input
                      type="radio"
                      name="cq_target_kind"
                      checked={!custom}
                      disabled={locked}
                      onChange={() => setCustom(false)}
                    />{' '}
                    {page.defaultTarget}
                  </label>
                  <label className="af-check">
                    <input type="radio" name="cq_target_kind" checked={custom} disabled={locked} onChange={() => setCustom(true)} />{' '}
                    another host
                  </label>
                  <input
                    type="text"
                    className="form-control input-sm cq-target"
                    name="target"
                    aria-label="Target host"
                    placeholder="host name or IP address"
                    hidden={!custom}
                    disabled={locked}
                    value={target}
                    onChange={(e) => setTarget(e.target.value)}
                  />
                  <span className="af-hint">a host name or an IP address - no http://, no path, no port</span>
                  {problem?.field === 'target' ? <span className="cq-error">{problem.message}</span> : null}
                </span>
              </div>
              <label className="af-row">
                <span className="af-k">Every</span>
                <span className="af-v">
                  <input
                    type="number"
                    className="form-control input-sm cq-interval"
                    name="interval_s"
                    min={page.limits.min}
                    max={page.limits.max}
                    disabled={locked}
                    value={interval}
                    onChange={(e) => setInterval(e.target.value)}
                  />
                  <span>seconds</span>
                  <span className="af-hint">
                    between {page.limits.min} and {page.limits.max}
                  </span>
                  {problem?.field === 'interval_s' ? <span className="cq-error">{problem.message}</span> : null}
                </span>
              </label>
              <div className="af-row cq-wifi-diag">
                <span className="af-k">Wi-Fi</span>
                <span className="af-v">
                  <label className="af-check">
                    <input
                      type="checkbox"
                      name="wifi_diag"
                      checked={wifiDiag}
                      disabled={locked}
                      onChange={(e) => setWifiDiag(e.target.checked)}
                    />{' '}
                    Allow Wi-Fi diagnostics
                  </label>
                  <span className="af-hint cq-wifi-diag-help">
                    <WifiDiagHelp doc={page.locationDoc} />
                  </span>
                </span>
              </div>
              <div className="af-row cq-speed-daily">
                <span className="af-k">Speed</span>
                <span className="af-v">
                  <label className="af-check">
                    <input
                      type="checkbox"
                      name="speed_auto"
                      checked={speedAuto}
                      disabled={locked}
                      onChange={(e) => setSpeedAuto(e.target.checked)}
                    />{' '}
                    Daily speed test
                  </label>
                  <span>at</span>
                  <SpeedHourSelect value={speedHour} disabled={locked || !speedAuto} onChange={setSpeedHour} />
                  <span>device local time</span>
                  <span className="af-hint cq-speed-help">{page.speedHelp ?? SPEED_HELP}</span>
                  {problem?.field === 'speed_hour' ? <span className="cq-error">{problem.message}</span> : null}
                </span>
              </div>
            </>
          )}
          {problem && problem.field !== 'target' && problem.field !== 'interval_s' && problem.field !== 'speed_hour' ? (
            <p className="cq-error">{problem.message}</p>
          ) : null}
          {page.canEdit ? (
            <div className="af-save">
              <button type="submit" className="btn btn-success btn-sm" disabled={saving}>
                <i className="fa fa-check"></i> Save
              </button>
            </div>
          ) : null}
        </form>
      </div>
    </div>
  )
}

function ModeChoice({
  value,
  mode,
  disabled,
  onPick,
  children,
}: {
  value: CqMode
  mode: CqMode
  disabled: boolean
  onPick(mode: CqMode): void
  children: ReactNode
}) {
  return (
    <label className="af-check">
      <input type="radio" name="cq_mode" value={value} checked={mode === value} disabled={disabled} onChange={() => onPick(value)} />{' '}
      {children}
    </label>
  )
}
